import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import BillingService, { InvoicePreview, InvoicePreviewParty } from '../../../services/BillingService';
import GuideButton from '../../common/guide/GuideButton';

const money = (n: number, currency: string) =>
  `${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

const PartyBlock: React.FC<{ label: string; party: InvoicePreviewParty }> = ({ label, party }) => (
  <Box sx={{ flex: 1, border: '1px solid #d7d2c8', p: 1.5, minWidth: 0 }}>
    <Typography variant="caption" sx={{ letterSpacing: 0.6, color: '#6b6560', fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{party.title || '—'}</Typography>
    <Typography variant="body2">VKN / TCKN: {party.taxNumber || '—'}</Typography>
    <Typography variant="body2">Vergi dairesi: {party.taxOffice || '—'}</Typography>
    <Typography variant="body2">Adres: {party.address || '—'}</Typography>
    <Typography variant="body2">Telefon: {party.phone || '—'}</Typography>
    <Typography variant="body2">E-posta: {party.email || '—'}</Typography>
  </Box>
);

interface Props {
  caseId: number | null;
  onClose: () => void;
  onSend: () => void;
  sending: boolean;
}

const InvoicePaperPreviewDialog: React.FC<Props> = ({ caseId, onClose, onSend, sending }) => {
  const paperRef = React.useRef<HTMLDivElement>(null);
  const [preview, setPreview] = React.useState<InvoicePreview | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!caseId) {
      setPreview(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    BillingService.getInvoicePreview(caseId)
      .then((data) => { if (!cancelled) setPreview(data); })
      .catch((e: any) => { if (!cancelled) setError(e?.message || 'Önizleme yüklenemedi.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [caseId]);

  const printPaper = () => {
    const node = paperRef.current;
    if (!node) return;
    const win = window.open('', '_blank', 'noopener,noreferrer,width=900,height=700');
    if (!win) {
      setError('Yazdırma penceresi açılamadı. Tarayıcı açılır pencereyi engelliyor olabilir.');
      return;
    }
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Fatura önizleme</title>
      <style>
        body { font-family: Arial, sans-serif; color: #111; margin: 24px; }
        h1 { font-size: 20px; margin: 0 0 8px; }
        .muted { color: #666; font-size: 12px; }
        .row { display: flex; gap: 12px; margin: 16px 0; }
        .box { flex: 1; border: 1px solid #ccc; padding: 10px; }
        table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; font-size: 12px; }
        th { background: #f4f1ea; text-align: left; }
        .right { text-align: right; }
        .totals { margin-top: 12px; margin-left: auto; width: 280px; }
        .totals div { display: flex; justify-content: space-between; padding: 4px 0; }
        .grand { font-weight: 700; font-size: 16px; border-top: 2px solid #111; margin-top: 4px; padding-top: 6px; }
      </style></head><body>${node.innerHTML}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <Dialog open={!!caseId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="span" sx={{ flex: 1 }}>Fatura önizlemesi</Box>
        <GuideButton guideKey="superadmin.invoicePaperPreview" size="small" />
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Alttaki düğme yalnız faturayı keser; e-fatura / e-arşiv gönderimini panelden elle yaparsınız.
        </Alert>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {preview && (
          <Box
            ref={paperRef}
            sx={{
              bgcolor: '#fffef8',
              border: '1px solid #e4dfd4',
              p: 2.5,
              color: '#1c1917',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.4 }}>SATIŞ FATURASI</Typography>
                <Typography variant="body2" color="text.secondary">Kağıt fatura önizlemesi · KDV dahil tutardan hesaplanmıştır</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2">Tarih: {new Date(preview.invoiceDate).toLocaleDateString('tr-TR')}</Typography>
                <Typography variant="body2">Belge no: {preview.invoiceNumber || 'Henüz kesilmedi'}</Typography>
                <Typography variant="body2">Ödeme: {preview.paymentMethod}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, my: 2, flexWrap: 'wrap' }}>
              <PartyBlock label="SATICI" party={preview.seller} />
              <PartyBlock label="ALICI" party={preview.buyer} />
            </Box>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& th, & td': { border: '1px solid #d7d2c8', p: 1, fontSize: 13 }, '& th': { bgcolor: '#f4f1ea', textAlign: 'left' } }}>
              <thead>
                <tr>
                  <th>Mal / Hizmet</th>
                  <th>Miktar</th>
                  <th>Birim fiyat (KDV hariç)</th>
                  <th>KDV %</th>
                  <th>KDV tutarı</th>
                  <th>Tutar (KDV dahil)</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.map((l, i) => (
                  <tr key={i}>
                    <td>{l.name}</td>
                    <td>{l.quantity} {l.unit}</td>
                    <td>{money(l.unitPriceExclVat, preview.currency)}</td>
                    <td>%{l.vatRate}</td>
                    <td>{money(l.vatAmount, preview.currency)}</td>
                    <td>{money(l.lineTotal, preview.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
            <Box sx={{ width: 280, ml: 'auto', mt: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Ara toplam</Typography>
                <Typography variant="body2">{money(preview.subTotal, preview.currency)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">KDV</Typography>
                <Typography variant="body2">{money(preview.vatTotal, preview.currency)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #1c1917', mt: 0.5, pt: 0.5 }}>
                <Typography sx={{ fontWeight: 800 }}>Genel toplam</Typography>
                <Typography sx={{ fontWeight: 800 }}>{money(preview.grandTotal, preview.currency)}</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
        <Button startIcon={<PrintIcon />} onClick={printPaper} disabled={!preview}>
          Yazdır / PDF
        </Button>
        <Button variant="contained" onClick={onSend} disabled={!preview || sending}>
          {sending ? 'Faturaya aktarılıyor...' : 'Faturaya aktar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePaperPreviewDialog;
